import * as React from 'react';
import {
GridRowModesModel,
DataGrid,
GridColDef,
} from '@mui/x-data-grid';
import Alert, { AlertProps } from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { RecordData } from './RecordData.tsx';
import { useNavigate } from 'react-router-dom';

// Temps de course (secondes) au rythme donne en minutes decimales par km
// sur `distanceKm`, formate en "3h32'05" ou "18'45".
function formaterTempsCourse(paceParKm: unknown, distanceKm: unknown): string {
  const pace = Number(paceParKm);
  const distance = Number(distanceKm);
  if (!isFinite(pace) || !isFinite(distance) || pace <= 0 || distance <= 0) return '';
  const totalSecondes = Math.round(pace * distance * 60);
  const heures = Math.floor(totalSecondes / 3600);
  const minutes = Math.floor((totalSecondes % 3600) / 60);
  const secondes = totalSecondes % 60;
  const ss = String(secondes).padStart(2, '0');
  if (heures > 0) {
    return `${heures}h${String(minutes).padStart(2, '0')}'${ss}"`;
  }
  return `${minutes}'${ss}"`;
}

function formaterDistance(distanceKm: unknown): string {
  const distance = Number(distanceKm);
  if (!isFinite(distance) || distance <= 0) return '';
  return `${distance.toLocaleString('fr-FR')} km`;
}

export default function AllureGrid({
  allure = {
    type: "VMA",
    hour: 0,
    minute: 3,
    second: 55,
  },
  handleLogout,
}: {allure?: RecordData; handleLogout?: () => void}) {

  const [isStarted, setStarted] = React.useState(allure);

  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});
 
  const [rows, setRows] = React.useState<RecordData[]>([]);
  
  const navigate = useNavigate();

  React.useEffect(() => {
      setStarted(allure);
    }, []);

    
  const readData = React.useCallback(
    (allure: RecordData) =>
      new Promise<RecordData[]>((resolve, reject) => {
        setTimeout(() => {
          console.log('=> Request on ' + allure);
          const token=sessionStorage.getItem('token') 
          const request = new Request(
            '/api/allure?type=' + allure.type + '&hour=' + allure.hour + '&minute=' + allure.minute + '&second=' + allure.second,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + token,
              },
            }
          );
          const fetchPromise = fetch(request);
          fetchPromise.then((response) => {
            if (response.status == 200) {
              setSnackbar({ children: 'Les zones d\'allure ont été calculées', severity: 'info' });
              resolve(response.json());
            } else {
              if (response.status == 403) {
                setSnackbar({ children: 'Accès refusé', severity: 'error' });
                handleLogout?.();
              } else {
                reject(new Error('Erreur lors du calcul des allures : ' + response.status + ' - ' + response.statusText));
              }              
            }
          });
        }, 200);
      }),
    []
  );

  React.useEffect(() => {    
    readData(allure)
      .then((data) => {
        if (data) {
          setRows(data);
        }
      })
      .catch((error: Error) => {
        // Erreur affichee dans l'ecran plutot que laissee non geree
        // (overlay d'erreur React en developpement).
        console.error(error);
        setSnackbar({ children: error.message, severity: 'error' });
      });
  }, [allure, readData]);
    
  const [snackbar, setSnackbar] = React.useState<Pick<
    AlertProps,
    'children' | 'severity'
    > | null>(null);


  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: '#',
      width: 250,
      hideable: true  
    },{
      // Libelle de la zone, tel que defini dans les parametres d'allures
      // de l'utilisateur (page Mes preferences).
      field: 'nom',
      headerName: 'Zone d\'allure',
      width: 200,
      hideable: false,
    },
    {
      field: 'paceStr',
      headerName: 'Allure',
      width: 100,
      align: 'right',
      headerAlign: 'right',
      hideable: false,
      renderCell: (params) => {
        const value = params.value;
        const isDefault = params.row?.defaut === true;
        return (
          <span style={{ fontWeight: isDefault ? 'bold' : 'normal' }}>
            {value}
          </span>
        );
      },
    },
    {
      field: 'distance',
      align: 'right',
      headerAlign: 'right',
      headerName: 'Distance',
      width: 100,
      hideable: false,
      valueFormatter: (value) => formaterDistance(value),
    },
    {
      // Temps de course sur la distance de la zone, a son allure.
      field: 'temps',
      align: 'right',
      headerAlign: 'right',
      headerName: 'Temps',
      width: 110,
      hideable: false,
      valueGetter: (_value, row) => formaterTempsCourse(row.pace, row.distance),
    },
    {
      field: 'pourcentage',
      align: 'right',
      headerAlign: 'right',
      headerName: '%VMA',
      type: 'number',
      width: 100,
      hideable: false,
      valueFormatter: (value) => (value === null || value === undefined ? '' : `${Number(value).toLocaleString('fr-FR')} %`),
    }
  ];

  const handleCloseSnackbar = () => setSnackbar(null);
  
  return (

    <div>  
        {!!snackbar && (
        <Snackbar
          open
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          onClose={handleCloseSnackbar}
          autoHideDuration={6000}
        >
          <Alert {...snackbar} onClose={handleCloseSnackbar} />
        </Snackbar>
      )}


      <DataGrid
        rows={rows}
        columns={columns}
        editMode="row"
        rowModesModel={rowModesModel}
        slotProps={{
          toolbar: { setRows, setRowModesModel },
        }}
        initialState={{
        columns: {
            columnVisibilityModel: {
              id: false,
            },
          },
        }}
        hideFooterPagination
        getRowId={(row) => row.id}
        // Le tri par colonne n'a pas de sens ici : les lignes representent
        // deja un ordre de zones d'allure (VMA -> RECUP) qu'il ne faut pas
        // melanger.
        disableColumnSorting
      />
    </div>
  );
}